import { Check, Minus, Crown, Tag } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const MODELS = [
  {
    id: "private",
    icon: Crown,
    name: "Private Label",
    line: "A product that is truly yours.",
    d: "We co-create or customise a formula exclusively for your brand — texture, actives, fragrance and claims tuned to your audience — then manufacture it under your name.",
    points: [
      "Custom or customised formula built for your brand",
      "Formula exclusive to your brand",
      "Bespoke packaging, label and carton design",
      "Stability & compatibility testing on your final pack",
      "Ideal for long-term brand equity and premium pricing",
    ],
  },
  {
    id: "white",
    icon: Tag,
    name: "White Label",
    line: "Proven formulas. Your brand. Fastest launch.",
    d: "Choose a ready, stability-tested formula from our 600+ library and launch it with your own branding — no R&D cycle, minimal investment, quickest time to market.",
    points: [
      "Pick from 600+ ready, proven formulations",
      "Your logo, label and packaging on our formula",
      "No development time — launch-ready quickly",
      "Lower upfront investment and risk",
      "Ideal for testing a market or expanding a range fast",
    ],
  },
];

const COMPARE = [
  ["Formula", "Developed / customised for you", "Ready formula from our library"],
  ["Exclusivity", "Exclusive to your brand", "Non-exclusive formula"],
  ["Customisation", "Texture, actives, fragrance, claims", "Fragrance & colour tweaks"],
  ["Speed to market", "Development + approval cycle", "Fastest — no R&D cycle"],
  ["Investment", "Higher — built for brand equity", "Lower — built for speed"],
  ["MOQ", "2,000 units India · 5,000 Global", "2,000 units India · 5,000 Global"],
  ["Best for", "D2C founders building a signature line", "New entrants, salons, range extensions"],
];

function ModelCard({ m, dark, delay }) {
  const Icon = m.icon;
  return (
    <Reveal delay={delay} data-testid={`label-model-${m.id}`}
      className={`group rounded-[2rem] p-8 lg:p-10 border relative overflow-hidden gold-hover-border ${dark ? "bg-[#1D3A2F] text-[#FDFBF7] border-[#1D3A2F]" : "bg-[#FDFBF7] border-[#D7D4CF]/70"}`}>
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#C9A961]/20 blur-3xl" />
      <div className="relative">
        <div className="w-14 h-14 rounded-2xl border gold-border flex items-center justify-center mb-8 transition-transform duration-500 group-hover:rotate-12">
          <Icon className="w-6 h-6 text-[#C9A961]" strokeWidth={1.4} />
        </div>
        <h3 className={`font-editorial text-4xl lg:text-5xl ${dark ? "" : "text-[#1D3A2F]"}`}>{m.name}</h3>
        <p className="font-editorial italic text-xl gold-text mt-2">{m.line}</p>
        <p className={`mt-5 leading-relaxed ${dark ? "text-[#FDFBF7]/75" : "text-[#222]/70"}`}>{m.d}</p>
        <ul className="mt-8 space-y-3">
          {m.points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm">
              <Check className="w-4 h-4 mt-0.5 text-[#C9A961] shrink-0" /> <span className={dark ? "text-[#FDFBF7]/85" : "text-[#1D3A2F]"}>{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
}

export function LabelModels() {
  return (
    <div data-testid="label-models">
      <div className="grid md:grid-cols-2 gap-6">
        <ModelCard m={MODELS[0]} dark delay={0} />
        <ModelCard m={MODELS[1]} delay={120} />
      </div>
      <Reveal className="mt-8 rounded-[2rem] bg-white/70 border border-[#C9A961]/35 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]" data-testid="label-compare-table">
            <thead>
              <tr className="bg-[#1D3A2F] text-[#F4E4BD]">
                <th className="text-left px-6 py-4 text-[10px] tracking-[0.22em] uppercase font-semibold">Compare</th>
                <th className="text-left px-6 py-4 font-editorial text-xl font-normal">Private Label</th>
                <th className="text-left px-6 py-4 font-editorial text-xl font-normal">White Label</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map(([k, a, b]) => (
                <tr key={k} className="border-t border-[#C9A961]/20 hover:bg-[#F7F4EE] transition-colors">
                  <td className="px-6 py-4 text-[11px] tracking-[0.18em] uppercase text-[#A68840] font-semibold whitespace-nowrap">{k}</td>
                  <td className="px-6 py-4 text-[#1D3A2F]">{a}</td>
                  <td className="px-6 py-4 text-[#1D3A2F]">{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-[#C9A961]/20 flex items-center gap-2 text-xs text-[#556B57]">
          <Minus className="w-3 h-3 text-[#C9A961]" /> Not sure which model fits? Book a free meeting and our team will recommend the right route.
        </div>
      </Reveal>
    </div>
  );
}
